/**
 * Analytics API Service
 * Read-only analytics endpoints for admin dashboard
 * All endpoints require admin role (403 for developer/viewer)
 */

import { apiClient } from './api.client';
import type {
  AnalyticsDateRangeParams,
  MetricsQueryParams,
  MetricsResponse,
  VoicebotQualityResponse,
  ChatbotQualityResponse,
  GlobalSummaryResponse,
  ApiResponse,
} from '@/types/analytics';

class AnalyticsApiService {
  /**
   * Get aggregated metrics for voicebots and chatbots
   *
   * @param params - Date range and optional filters
   * @returns Pre-calculated daily metrics from aggregated_metrics table
   *
   * Endpoint: GET /api/v1/analytics/metrics
   * Auth: Admin only
   * Performance: <50ms at p95
   */
  async getMetrics(params: MetricsQueryParams): Promise<MetricsResponse> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });

    if (params.clientId) {
      queryParams.append('clientId', params.clientId);
    }

    if (params.metricType) {
      queryParams.append('metricType', params.metricType);
    }

    const response = await apiClient.get<ApiResponse<MetricsResponse>>(
      `/analytics/metrics?${queryParams.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch metrics');
    }

    return response.data.data;
  }

  /**
   * Get AI-generated quality analysis for voicebot calls
   *
   * @param params - Date range and optional client filter
   * @returns Quality analysis records with success scores, outcomes, sentiment
   *
   * Endpoint: GET /api/v1/analytics/voicebot/quality
   * Auth: Admin only
   * Data Source: voicebot_analysis table (populated weekly by Mastra workflow)
   */
  async getVoicebotQuality(params: AnalyticsDateRangeParams): Promise<VoicebotQualityResponse> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });

    if (params.clientId) {
      queryParams.append('clientId', params.clientId);
    }

    const response = await apiClient.get<ApiResponse<VoicebotQualityResponse>>(
      `/analytics/voicebot/quality?${queryParams.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch voicebot quality analysis');
    }

    return response.data.data;
  }

  /**
   * Get AI-generated quality analysis for chatbot conversations
   *
   * @param params - Date range and optional client filter
   * @returns Quality analysis records with quality scores, resolution status, engagement
   *
   * Endpoint: GET /api/v1/analytics/chatbot/quality
   * Auth: Admin only
   * Data Source: chatbot_analysis table (populated weekly by Mastra workflow)
   */
  async getChatbotQuality(params: AnalyticsDateRangeParams): Promise<ChatbotQualityResponse> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });

    if (params.clientId) {
      queryParams.append('clientId', params.clientId);
    }

    const response = await apiClient.get<ApiResponse<ChatbotQualityResponse>>(
      `/analytics/chatbot/quality?${queryParams.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch chatbot quality analysis');
    }

    return response.data.data;
  }

  /**
   * Get consolidated global summary combining metrics and quality analysis
   *
   * @param params - Date range and optional client filter
   * @returns Combined summary with voicebot, chatbot, and global KPIs
   *
   * Endpoint: GET /api/v1/analytics/summary
   * Auth: Admin only
   * Implementation: Parallel queries using Promise.all() for optimal performance
   * Use Case: Dashboard overview with key performance indicators
   */
  async getSummary(params: AnalyticsDateRangeParams): Promise<GlobalSummaryResponse> {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });

    if (params.clientId) {
      queryParams.append('clientId', params.clientId);
    }

    const response = await apiClient.get<ApiResponse<GlobalSummaryResponse>>(
      `/analytics/summary?${queryParams.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch analytics summary');
    }

    return response.data.data;
  }
}

export const analyticsApiService = new AnalyticsApiService();
